import { ReactNode } from 'react';
import { IControlProps, IPartialFormGroup } from '../types';
import FormControl from '../FormControl';
import YearField, { IYearFieldProps } from '../../atoms/YearField';
import { useHookFormController } from '../../hooks/useHookFormController';
import { isoDateToYear, yearToIsoDate } from '../../lib/dateFormValue';

export type IYearFieldControlProps = Omit<IYearFieldProps, 'value' | 'onChange' | 'defaultValue' | 'type'> &
    IControlProps & {
        formGroupProps?: IPartialFormGroup;
        label?: ReactNode;
        tooltip?: ReactNode;
        required?: boolean;
        min?: number;
        max?: number;
        onValueChange?: (value: number | null) => void;
    };

const YearFieldControl = ({
    name,
    min,
    max,
    rules,
    defaultValue,
    control,
    shouldUnregister,
    label,
    tooltip,
    required,
    formGroupProps,
    onValueChange,
    readOnly,
    ...props
}: IYearFieldControlProps) => {
    const Controller = useHookFormController();
    const minDate = min ? `${min}-01-01` : undefined;
    const maxDate = max ? `${max}-12-31` : undefined;

    return (
        <Controller
            control={control}
            name={name}
            rules={rules}
            shouldUnregister={shouldUnregister}
            defaultValue={defaultValue}
            render={({ field: { onChange, value, ...fieldProps } }) => {
                const currentIso = yearToIsoDate(value as number | string | null);

                return (
                    <FormControl
                        readonlyAppearance={readOnly || props.disabled ? 'field' : undefined}
                        name={name}
                        label={label}
                        tooltip={tooltip}
                        required={required}
                        control={control}
                        {...formGroupProps}
                    >
                        <YearField
                            {...fieldProps}
                            {...props}
                            readOnly={readOnly}
                            minDate={minDate}
                            maxDate={maxDate}
                            value={currentIso}
                            onChange={(next) => {
                                if (readOnly) return;
                                const iso = typeof next === 'string' ? next : null;
                                if (iso === currentIso) return;
                                const year = isoDateToYear(iso);
                                onChange(year);
                                if (onValueChange) onValueChange(year);
                            }}
                        />
                    </FormControl>
                );
            }}
        />
    );
};

export default YearFieldControl;
